import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { useAuthState } from "@/hooks/useAuthState";
import { useDevImpersonationStorage } from "@/hooks/useDevImpersonationStorage";
import type { DevImpersonationV1 } from "@/lib/devImpersonationStorage";
import type { AuthProfile, AccessStatus, OrgRole, ActiveMembership, PlatformRole } from "@/hooks/useAuthState";
import type { OrgRoleEntry } from "@/hooks/authStateModel";

export type AuthImpersonationInfo = {
  active: boolean;
  roles: string[];
  workspaceBrowseAllOrgs: boolean;
};

/** Fields shared by every status variant of AuthContextValue. */
type SharedAuthContextFields = {
  accessStatus: AccessStatus;
  activeOrganizationId: string | null;
  activeLocationId: string | null;
  accessibleLocationIds: string[];
  memberOrganizationIds: string[];
  activeMembership: ActiveMembership | null;
  roles: string[];
  role: OrgRole;
  platformRole: PlatformRole;
  /** Org-scoped role rows (org_id + role). Excludes platform_* rows. */
  orgRoles: OrgRoleEntry[];
  /** Identity-level platform_* role strings. `platformRole` is the highest of these. */
  platformRoles: string[];
  /**
   * `organizations.has_sales_floor` for the active org (Fulfillment
   * Slice 3). Drives the bucket nav-visibility gate + daily-checklist
   * intake-nag enable; replaces the Slice-1 VITE_FULFILLMENT_PILOT
   * scaffold flag. Defaults `false` while auth is loading (fail closed).
   */
  hasSalesFloor: boolean;
  /**
   * `organizations.services_enabled` for the active org (Service v1.0).
   * Gates operational Service bucket modes + dispatch APIs. Defaults
   * `false` while auth is loading (fail closed).
   */
  servicesEnabled: boolean;
  /**
   * `organizations.personal_assistant_enabled` for the active org (PA-1a).
   * Gates Personal Assistant mode shell. Defaults `false` while auth is
   * loading (fail closed).
   */
  personalAssistantEnabled: boolean;
  isPlatformOperator: boolean;
  isRoleUnassigned: boolean;
  isAdmin: boolean;
  /** Union check across org + platform tokens (backward compatible). */
  hasRole: (...r: string[]) => boolean;
  /** Identity-only check — true iff the user actually holds the platform role (ignores org tokens + impersonation). */
  hasPlatformRole: (...r: string[]) => boolean;
  /** Role checks ignoring dev impersonation overlay (gate the tool + workspace browse). */
  realHasRole: (...r: string[]) => boolean;
  impersonation: AuthImpersonationInfo;
  refreshProfile: () => void;
};

export type AuthContextValue =
  | (SharedAuthContextFields & {
      /** No Supabase config; app runs without auth. */
      status: "no-supabase";
      session: null;
      profile: null;
      error: undefined;
    })
  | (SharedAuthContextFields & {
      status: "idle" | "loading";
      session: Session | null;
      profile: AuthProfile | null;
      error: undefined;
    })
  | (SharedAuthContextFields & {
      status: "ready";
      session: Session | null;
      profile: AuthProfile | null;
      error: undefined;
    })
  | (SharedAuthContextFields & {
      status: "error";
      session: Session | null;
      profile: AuthProfile | null;
      error: string;
    });

const AuthContext = createContext<AuthContextValue | null>(null);

/** Split comma/slash/pipe-separated role labels and trim (e.g. legacy "sales, trainer"). */
function grantedRoleTokens(
  allRoles: string[],
  primaryRole: OrgRole,
  platformRoles: string[]
): Set<string> {
  const set = new Set<string>();
  const add = (raw: string | null | undefined) => {
    if (raw == null || raw === "") return;
    for (const part of String(raw).split(/[,/|]+/)) {
      const t = part.trim().toLowerCase();
      if (t) set.add(t);
    }
  };
  for (const g of allRoles) add(g);
  if (primaryRole !== "unassigned") add(primaryRole);
  // Defense-in-depth: ensure every platform_* string lands in the union even
  // if a misfiled row caused it to be missing from allRoles for some reason.
  for (const p of platformRoles) add(p);
  return set;
}

function impersonationAllowed(realHasRole: (...r: string[]) => boolean): boolean {
  return realHasRole("platform_dev", "dev");
}

/** Legacy `user_roles.role` values `org_owner` / `org_admin` align with `owner` / `admin` for permission checks. */
function roleTokenMatches(tokens: Set<string>, role: string): boolean {
  const k = String(role).trim().toLowerCase();
  if (tokens.has(k)) return true;
  // role_taxonomy §5.1 — platform_owner/admin/dev satisfy every org-scoped gate.
  // (Impersonation replaces effectiveTokens, so this does not leak through a mask.)
  if (
    !k.startsWith("platform_") &&
    (tokens.has("platform_owner") || tokens.has("platform_admin") || tokens.has("platform_dev"))
  ) {
    return true;
  }
  if (tokens.has("platform_owner") && (k === "platform_admin" || k === "platform_dev" || k === "platform_trainer")) {
    return true;
  }
  if (k === "owner" && tokens.has("org_owner")) return true;
  if (k === "admin" && tokens.has("org_admin")) return true;
  if (k === "org_owner" && tokens.has("owner")) return true;
  if (k === "org_admin" && tokens.has("admin")) return true;
  return false;
}

function primaryOrgRoleFromTokens(tokens: Set<string>): OrgRole {
  // Precedence is umbrella-tokens first (preserves today's primary-role
  // banner labels for multi-token users — e.g. a `manufacturing` +
  // `mfg_accepter` user still surfaces as "Manufacturing"), then the
  // carved sub-role tokens per docs/design/role_taxonomy.md §4 in a
  // pragmatic order (manager > rep > author > accepter > coordinator >
  // driver / third-party-hauler at the tail since those surfaces don't
  // exist yet). A sub-role-only user (e.g. impersonation of `sales_rep`
  // alone) now resolves to that sub-role for the auth.role banner
  // instead of falling through to "unassigned".
  const order: OrgRole[] = [
    "owner",
    "admin",
    "dev",
    "trainer",
    "marketing",
    "manufacturing",
    "manufacturing_plant_manager",
    "manufacturing_plant_supervisor",
    "manufacturing_plant",
    "manufacturing_purchasing",
    "mfg_qc",
    "mfg_final_qc",
    "manufacturing_repair_technician",
    "ads_guidance_editor",
    "sales",
    "sales_manager",
    "sales_rep",
    "training_author",
    "ops_reviewer",
    "mfg_accepter",
    "production_operator",
    "logistics_coordinator",
    "driver",
    "third_party_hauler_subcoordinator",
  ];
  for (const o of order) {
    if (tokens.has(o)) return o;
  }
  // `manufacturing_order_processor` is the alternate spelling of
  // `mfg_accepter` per role-taxonomy §5.4; map it to the canonical
  // token here so the banner shows "Mfg accepter".
  if (tokens.has("manufacturing_order_processor")) return "mfg_accepter";
  // `dispatcher` is the alias of `logistics_coordinator` per §5.3.
  if (tokens.has("dispatcher")) return "logistics_coordinator";
  if (tokens.has("org_owner")) return "owner";
  if (tokens.has("org_admin")) return "admin";
  return "unassigned";
}

function platformRoleFromTokens(tokens: Set<string>): PlatformRole {
  if (tokens.has("platform_owner")) return "platform_owner";
  if (tokens.has("platform_admin")) return "platform_admin";
  if (tokens.has("platform_dev")) return "platform_dev";
  if (tokens.has("platform_trainer")) return "platform_trainer";
  return null;
}

function activeMembershipRoleOk(role: OrgRole): role is ActiveMembership["role"] {
  return role !== "unassigned";
}

function deriveValue(
  state: ReturnType<typeof useAuthState>,
  imp: DevImpersonationV1
): AuthContextValue {
  const activeOrganizationId = state.activeMembership?.organization_id ?? state.profile?.primary_organization_id ?? null;
  const activeLocationId = state.profile?.active_location_id ?? null;
  const accessibleLocationIds = state.accessibleLocationIds ?? [];
  const baseRole = state.role;
  const baseRoles = state.allRoles ?? [];
  const basePlatformRole = state.platformRole;
  const basePlatformRoles = state.platformRoles ?? [];
  const baseOrgRoles = state.orgRoles ?? [];
  const baseTokens = grantedRoleTokens(baseRoles, baseRole, basePlatformRoles);
  const realHasRole = (...r: string[]) => r.some((x) => roleTokenMatches(baseTokens, String(x)));
  // hasPlatformRole is identity-only by design: it is NOT affected by dev
  // impersonation. The realHasRole gate already covers "is this user allowed
  // to impersonate at all"; downstream code uses hasPlatformRole when it
  // needs the real underlying identity (e.g. workspace-org override).
  const platformRolesLower = basePlatformRoles.map((p) => p.trim().toLowerCase());
  const hasPlatformRole = (...r: string[]) => {
    const wanted = r.map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (wanted.length === 0) return false;
    return wanted.some((w) => platformRolesLower.includes(w));
  };

  const useImpersonation =
    imp.enabled && imp.roles.length > 0 && state.status === "ready" && impersonationAllowed(realHasRole);

  let effectiveTokens = baseTokens;
  let rolesOut = baseRoles;
  let role: OrgRole = baseRole;
  let platformRole: PlatformRole = basePlatformRole;
  let activeMembership: ActiveMembership | null = state.activeMembership ?? null;

  if (useImpersonation) {
    effectiveTokens = new Set(imp.roles);
    rolesOut = [...effectiveTokens].sort();
    role = primaryOrgRoleFromTokens(effectiveTokens);
    platformRole = platformRoleFromTokens(effectiveTokens);
    if (activeMembership && role !== "unassigned" && activeMembershipRoleOk(role)) {
      activeMembership = { organization_id: activeMembership.organization_id, role };
    }
  }

  const isPlatformOperator = platformRole !== null;
  const isRoleUnassigned =
    state.accessStatus === "approved" &&
    !(state.activeMembership || (useImpersonation && role !== "unassigned"));

  const isAdmin =
    effectiveTokens.has("admin") ||
    effectiveTokens.has("owner") ||
    effectiveTokens.has("dev") ||
    effectiveTokens.has("org_admin") ||
    effectiveTokens.has("org_owner");
  const hasRole = (...r: string[]) => r.some((x) => roleTokenMatches(effectiveTokens, String(x)));

  const impersonation: AuthImpersonationInfo = {
    active: !!useImpersonation,
    roles: useImpersonation ? imp.roles : [],
    workspaceBrowseAllOrgs: !!useImpersonation && imp.workspaceBrowseAllOrgs,
  };

  return {
    status: state.status,
    session: state.session,
    profile: state.profile,
    accessStatus: state.accessStatus,
    activeOrganizationId,
    activeLocationId,
    accessibleLocationIds,
    memberOrganizationIds: state.memberOrganizationIds ?? [],
    activeMembership,
    roles: rolesOut,
    role,
    platformRole,
    orgRoles: baseOrgRoles,
    platformRoles: basePlatformRoles,
    hasSalesFloor: state.hasSalesFloor ?? false,
    servicesEnabled: state.servicesEnabled ?? false,
    personalAssistantEnabled: state.personalAssistantEnabled ?? false,
    isPlatformOperator,
    isRoleUnassigned,
    isAdmin,
    hasRole,
    hasPlatformRole,
    realHasRole,
    impersonation,
    refreshProfile: state.refreshProfile,
    error: state.status === "error" ? (state as { error: string }).error : undefined,
  } as AuthContextValue;
}

export function AuthProvider(props: { children: ReactNode }) {
  const state = useAuthState();
  const imp = useDevImpersonationStorage();
  const value = useMemo(() => deriveValue(state, imp), [state, imp]);

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

/** Use auth only when AuthProvider may be absent (e.g. dev); returns null if no provider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}
